import type { FinancialInstrument, UnderlyingAsset } from 'state';
import { CREATING, FINISHED, FROZEN, PAUSED } from 'state';

export const MARKETS_V2_SESSION_ID = 23;
export const MARKETS_V2_STORAGE_KEY = 'pm.markets.session-23.experience';

export type MarketsExperience = 'classic' | 'board';

export type Session23MarketGroups = {
  fx: FinancialInstrument[];
  etfs: FinancialInstrument[];
  options: FinancialInstrument[];
  bonds: FinancialInstrument[];
};

const ETF_RESOURCE_TYPES = new Set([
  'ETF',
  'ETFEquities',
  'ETFCommodities',
  'ETFBonds',
]);

export const supportsMarketsV2 = (sessionId?: number) =>
  sessionId === MARKETS_V2_SESSION_ID;

export const groupSession23Markets = (
  instruments: FinancialInstrument[],
  assets: Record<number, UnderlyingAsset>,
): Session23MarketGroups =>
  instruments.reduce<Session23MarketGroups>(
    (groups, instrument) => {
      const asset = assets[instrument.underlying_asset];
      if (!asset || instrument.fx_hedge) return groups;

      if (asset.resourcetype === 'FX') groups.fx.push(instrument);
      if (ETF_RESOURCE_TYPES.has(asset.resourcetype)) {
        groups.etfs.push(instrument);
      }
      if (asset.resourcetype === 'SimplifiedOption') {
        groups.options.push(instrument);
      }
      if (asset.resourcetype === 'AFSBond') groups.bonds.push(instrument);
      return groups;
    },
    { fx: [], etfs: [], options: [], bonds: [] },
  );

export const instrumentHasPosition = (instrument: FinancialInstrument) =>
  instrument.simplified_option
    ? instrument.simplified_option.contracts !== 0
    : instrument.amount !== 0;

export const ordinaryQuoteIsAvailable = (asset: UnderlyingAsset) =>
  Number(asset.bid) > 0 && Number(asset.ask) > 0;

export const sessionBlocksTrading = (status: number) =>
  [PAUSED, CREATING, FINISHED, FROZEN].includes(status);

export const ordinaryTradeIsDisabled = ({
  status,
  asset,
  instrument,
  closedFromInterval,
}: {
  status: number;
  asset: UnderlyingAsset;
  instrument: FinancialInstrument;
  closedFromInterval: boolean;
}) =>
  sessionBlocksTrading(status) ||
  closedFromInterval ||
  !ordinaryQuoteIsAvailable(asset) ||
  (asset.resourcetype === 'AFSBond' &&
    instrument.htm_afs_years_to_maturity === 0);

export const readMarketsExperience = (): MarketsExperience => {
  try {
    return window.localStorage.getItem(MARKETS_V2_STORAGE_KEY) === 'board'
      ? 'board'
      : 'classic';
  } catch (_error) {
    return 'classic';
  }
};

export const saveMarketsExperience = (experience: MarketsExperience) => {
  try {
    window.localStorage.setItem(MARKETS_V2_STORAGE_KEY, experience);
  } catch (_error) {
    // Storage can be unavailable in hardened/private browser contexts. The
    // in-memory choice still works for the current page.
  }
};
